/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Grid, Box, Container, useMediaQuery, useTheme, Typography, Skeleton } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { useLazyGetGuaranteeConfigQuery } from "@api/catalogs";
import { GuaranteeConfigStruture } from "@interfaces/catalogs";
import { GUARANTEE_TYPE } from "@helpers/constantes";
import FormGarantiaInmobiliaria from "@components/Garantia/FormGarantiaInmobiliaria";
import FormGarantiaVehicular from "@components/Garantia/FormGarantiaVehicular";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";

const SCREEN_STATUS = "CSIGA";

type GarantiaFormProps = React.ComponentProps<typeof FormGarantiaVehicular>;
type GarantiaFormComponent = React.ComponentType<GarantiaFormProps>;

const guaranteeFormByCode: Record<string, GarantiaFormComponent> = {
  [GUARANTEE_TYPE.vehicular.code]: FormGarantiaVehicular,
  [GUARANTEE_TYPE.prendaria.code]: FormGarantiaVehicular,
};

const guaranteeLabels: Record<string, string> = {
  [GUARANTEE_TYPE.vehicular.code]: "Vehicular",
  [GUARANTEE_TYPE.prendaria.code]: "Prendaria",
};

const taxSystem_can_add_garantia = ["CSK01", "CSK02"];

const InformacionGarantia = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();

  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();

  const [guaranteeConfig, setGuaranteeConfig] = useState<GuaranteeConfigStruture | null>(null);
  const [guaranteeConfigList, setGuaranteeConfigList] = useState<GuaranteeConfigStruture[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [canAdd, setCanAdd] = useState(false);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getGuaranteeTypeConfig({ tax_code: applicationData.type }).unwrap();
        const application = await getLoanAplication(applicationData.id).unwrap();
        const guaranteeTypeId = application?.guarantee_types?.length ? String(application.guarantee_types[0]) : null;
        const guaranteeConfig = guaranteeTypeId
          ? response.find((item) => item.guarantee_type_id === guaranteeTypeId)
          : undefined;
        setGuaranteeConfig(guaranteeConfig || null);
        setGuaranteeConfigList(response);
        setCanAdd(taxSystem_can_add_garantia.includes(application.tax_systems?.code ?? ""));
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionGarantia",
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSelectedGuaranteeType = (valor: string) => {
    const nuevoConfig = guaranteeConfigList.find((item) => item.guarantee_type_id === valor);

    if (nuevoConfig) {
      setGuaranteeConfig(nuevoConfig);
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.informacion_garantia?.titulo || " Información de tu garantía"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            {isLoading ? (
              <Skeleton variant="text" width="100%" height={200} />
            ) : (
              <React.Fragment>
                {(() => {
                  const guaranteeCode = guaranteeConfig?.guarantee_type?.code ?? "";
                  const SelectedForm = guaranteeFormByCode[guaranteeCode] ?? FormGarantiaInmobiliaria;
                  const guaranteeLabel = guaranteeLabels[guaranteeCode] ?? "";

                  return (
                    <SelectedForm
                      nextStatus={nextStatus}
                      guarantee_type_id={guaranteeConfig?.guarantee_type_id ?? null}
                      guaranteeConfigList={guaranteeConfigList}
                      onSeleccion={handleSelectedGuaranteeType}
                      guaranteeLabel={guaranteeLabel}
                      canAdd={canAdd}
                    />
                  );
                })()}
              </React.Fragment>
            )}
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionGarantia;
